// =============================================================================
// useShellCounts - the staff shell's shared background counts (round 4 #3,
// round 7 #2).
//
//   useShellFeed({ navIds, inboundSms, tier })
//     Round 7 #2. Exactly one caller (Layout). Polls the ONE shell read
//     (shell_counts, lib/shellCounts.js) every 60s, also in a background tab
//     (the bell's desktop alert needs it), and copies each value into the
//     cache entry its consumers read. Nothing else in the shell polls: a
//     staff tab used to send 5-9 count requests a minute, hidden or not.
//
//   useInboxCounts({ enabled, live })
//     The Inbox open-item counts (inbox_feed_counts) from the one shared cache
//     entry the Inbox page also reads (lib/shellCounts.js), so the sidebar
//     badge, the bell and the page subtitle always agree. Exactly one caller
//     (Layout) passes live: it refreshes on a realtime change to inbox_items
//     (private channel, debounced). The 60s refresh is the shell read's.
//     Nothing refetches on navigation.
//
//   useShellCount(key, read, { enabled, scope })
//     A shared count (insurance updates, ...). The same key from two
//     components (the sidebar badge and the bell) is one entry, filled by
//     the shell read; `read` runs only when that answer has no value for it.
//
// Both return { value, error, refetch }: value stays null until the first
// answer and keeps the last good value after a failed refresh; error says the
// read failed, so a caller never shows a failure as a zero.
// =============================================================================
import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { queryClient } from "../lib/queryClient";
import { useAuth } from "../auth/AuthProvider";
import { privateChannel } from "../lib/realtime/presence";
import { practiceTopic } from "../lib/realtime/presenceCore";
import { practiceTzOf } from "../lib/practiceDay";
import { INBOX_DOCS_ONLY_ROLES } from "../lib/tokens";
import {
  INBOX_COUNTS_KEY, readInboxCounts, fetchInboxCounts, shellCountKey, inboxOpenCount,
  shellAllKey, SHELL_POLL_MS, readShellCounts, readShellKey, shellKeyFor, setShellFeed, shellFeed,
} from "../lib/shellCounts";
import { shellWants, SHELL_KEYS } from "../lib/shellCountsCore";

// Realtime bursts (a bulk Done on 20 rows) settle before one re-read. The
// Inbox page re-reads its counts 400ms after the same burst; a result that
// fresh is reused instead of asked for again (REUSE_MS < DEBOUNCE_MS).
const DEBOUNCE_MS = 1500;
const REUSE_MS = 1400;

export function useShellFeed({ navIds = null, inboundSms = false, tier = null } = {}) {
  const { user, profile, practiceId, role, spectator } = useAuth();
  // The session's user id is known at once; the profile row (role, provider,
  // practice zone) loads a moment later. The shell read needs only the
  // session, so the badges never wait for the profile or read themselves.
  const uid = user?.id || profile?.id || null;
  const navKey = navIds ? Array.from(navIds).sort().join(",") : "";
  // The per-badge reads (before the migration) need the profile ("mine"
  // tasks use its role, notes its practice zone); until it loads the poll
  // makes none and each badge reads itself.
  const wants = useMemo(() => (profile ? shellWants({
    navIds: navKey ? navKey.split(",") : [], role, profileRole: profile.role || null, uid,
    providerId: profile.provider_id || null, inboundSms, tier, docsOnlyRoles: INBOX_DOCS_ONLY_ROLES,
  }) : []), [navKey, role, profile, uid, inboundSms, tier]);
  const allKey = useMemo(() => shellAllKey(practiceId, uid), [practiceId, uid]);
  // The context the shell read and the badges' own reads use. A spectator
  // session (a super admin viewing another practice) keeps the per-badge
  // reads: the function scopes to the JWT's own practice.
  const ctx = {
    practiceId: practiceId || null, uid, role: profile?.role || null, providerId: profile?.provider_id || null,
    tz: practiceTzOf(profile), wants, perBadgeOnly: !!spectator,
  };
  // Registered during render (not in an effect) so the bell and the badges,
  // which render after this hook, already find it on their first fetch.
  // The mount effect sets it again after a (Strict Mode) remount and clears
  // it on sign-out, so a later session never reads with this one's context.
  // (Not cleared on every render: child effects run before this one, and a
  // badge fetching in between would find no feed and read itself.)
  const feedRef = useRef(null);
  feedRef.current = { key: allKey, ctx };
  setShellFeed(feedRef.current);
  useEffect(() => {
    setShellFeed(feedRef.current);
    return () => setShellFeed(null);
  }, []);

  const on = !!practiceId && !!uid;
  const q = useQuery({
    queryKey: allKey,
    queryFn: () => readShellCounts((shellFeed() || { ctx }).ctx),
    enabled: on,
    staleTime: SHELL_POLL_MS,
    refetchInterval: SHELL_POLL_MS,
    // The one read that keeps going in a background tab (desktop alert).
    refetchIntervalInBackground: true,
    retry: 0,
  });

  // Copy each answer into the entries the consumers read. A badge the answer
  // has no value for (a role preview, a part that failed on the server) is
  // asked to read itself, at the same once-a-minute pace.
  useEffect(() => {
    if (!q.data) return;
    const values = q.data.values || {};
    for (const k of SHELL_KEYS) {
      const entryKey = shellKeyFor(k, ctx);
      if (values[k] != null) { queryClient.setQueryData(entryKey, values[k]); continue; }
      if (!wants.includes(k)) continue;
      // Not when it has just read itself (its first load does, when the
      // answer lacks it), so one poll is at most one read per badge.
      const st = queryClient.getQueryState(entryKey);
      if (st && (st.fetchStatus === "fetching" || Date.now() - Math.max(st.dataUpdatedAt, st.errorUpdatedAt) < SHELL_POLL_MS / 2)) continue;
      queryClient.invalidateQueries({ queryKey: entryKey, exact: true, refetchType: "active" });
    }
  }, [q.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // A failed shell read: every badge shows its error state (and keeps its
  // last good number), as each did when its own read failed. Set directly,
  // not by refetching, so a failed poll costs no further requests; Retry
  // (a refetch) asks the shell read again.
  useEffect(() => {
    if (!q.errorUpdatedAt || !q.error) return;
    for (const k of wants) {
      const query = queryClient.getQueryCache().find({ queryKey: shellKeyFor(k, ctx), exact: true });
      if (!query || query.state.fetchStatus === "fetching") continue;
      query.setState({ status: "error", error: q.error, errorUpdatedAt: Date.now(), errorUpdateCount: query.state.errorUpdateCount + 1 });
    }
  }, [q.errorUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  return { error: q.isError ? q.error : null, source: q.data ? q.data.source : null };
}

export function useInboxCounts({ enabled = true, live = false, practiceId = null } = {}) {
  const on = !!enabled;
  const q = useQuery({
    queryKey: INBOX_COUNTS_KEY,
    queryFn: () => readShellKey("inbox", readInboxCounts),
    enabled: on,
    // Refreshed by the shell read (useShellFeed) and the realtime nudge
    // below, never by its own timer.
    staleTime: SHELL_POLL_MS,
    retry: 0,
  });

  useEffect(() => {
    if (!live || !on || !practiceId) return undefined;
    let t = null;
    const ch = privateChannel(practiceTopic(practiceId, "inbox-counts-shell"))
      .on("postgres_changes", { event: "*", schema: "public", table: "inbox_items", filter: "practice_id=eq." + practiceId }, () => {
        clearTimeout(t);
        t = setTimeout(() => {
          fetchInboxCounts({ maxAgeMs: REUSE_MS }).catch((e) => console.warn("[shell] inbox counts refresh failed:", e && e.message));
        }, DEBOUNCE_MS);
      })
      .subscribe();
    return () => { clearTimeout(t); supabase.removeChannel(ch); };
  }, [live, on, practiceId]);

  return {
    counts: q.data || null,
    value: on ? inboxOpenCount(q.data) : null,
    error: on && q.isError ? q.error : null,
    refetch: q.refetch,
  };
}

export function useShellCount(key, read, { enabled = true, scope = [] } = {}) {
  const q = useQuery({
    queryKey: shellCountKey(key, ...scope),
    queryFn: () => readShellKey(key, read),
    enabled: !!enabled,
    staleTime: SHELL_POLL_MS,
    retry: 0,
  });
  return {
    value: enabled && q.data != null ? q.data : null,
    error: enabled && q.isError ? q.error : null,
    refetch: q.refetch,
  };
}
